import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const HOME_TAB_KEY = '/groups';
export const WORKSPACE_TABS_STORAGE_KEY = 'arcmgr-workspace-tabs-v1';

const defaultWorkspaceTabs = () => ({
  openedKeys: [HOME_TAB_KEY],
  lastUrls: {[HOME_TAB_KEY]: HOME_TAB_KEY},
});

const arraysEqual = (left, right) => (
  left.length === right.length && left.every((value, index) => value === right[index])
);

const objectsEqual = (left, right) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length
    && leftKeys.every(key => left[key] === right[key]);
};

const normalizePersistedState = (persistedState, currentState) => {
  if (!persistedState || typeof persistedState !== 'object') {
    return currentState;
  }

  return {
    ...currentState,
    openedKeys: Array.isArray(persistedState.openedKeys)
      ? persistedState.openedKeys
      : currentState.openedKeys,
    lastUrls: persistedState.lastUrls && typeof persistedState.lastUrls === 'object'
      ? persistedState.lastUrls
      : currentState.lastUrls,
  };
};

const useWorkspaceTabs = create(persist((set) => ({
  ...defaultWorkspaceTabs(),

  reconcileLocation: (key, url) => set((state) => {
    const openedKeys = state.openedKeys.includes(key)
      ? state.openedKeys
      : [...state.openedKeys, key];
    const lastUrls = state.lastUrls[key] === url
      ? state.lastUrls
      : {...state.lastUrls, [key]: url};

    if (openedKeys === state.openedKeys && lastUrls === state.lastUrls) {
      return state;
    }
    return {openedKeys, lastUrls};
  }),

  sanitizeTabs: (allowedKeys, isUrlForKey) => set((state) => {
    const allowed = new Set(allowedKeys);
    allowed.add(HOME_TAB_KEY);

    const openedKeys = [HOME_TAB_KEY];
    const seen = new Set(openedKeys);
    state.openedKeys.forEach((key) => {
      if (typeof key === 'string' && allowed.has(key) && !seen.has(key)) {
        seen.add(key);
        openedKeys.push(key);
      }
    });

    const lastUrls = {};
    openedKeys.forEach((key) => {
      const candidate = state.lastUrls?.[key];
      lastUrls[key] = typeof candidate === 'string' && isUrlForKey(key, candidate)
        ? candidate
        : key;
    });

    if (arraysEqual(openedKeys, state.openedKeys) && objectsEqual(lastUrls, state.lastUrls)) {
      return state;
    }
    return {openedKeys, lastUrls};
  }),

  closeTab: (key) => {
    if (key === HOME_TAB_KEY) {
      return null;
    }

    let nextKey = null;
    set((state) => {
      const index = state.openedKeys.indexOf(key);
      if (index === -1) {
        return state;
      }

      const openedKeys = state.openedKeys.filter(openedKey => openedKey !== key);
      const lastUrls = {...state.lastUrls};
      delete lastUrls[key];
      nextKey = openedKeys[Math.max(0, index - 1)] || HOME_TAB_KEY;
      return {openedKeys, lastUrls};
    });
    return nextKey;
  },

  resetTabs: () => set(defaultWorkspaceTabs()),
}), {
  name: WORKSPACE_TABS_STORAGE_KEY,
  storage: createJSONStorage(() => sessionStorage),
  version: 1,
  migrate: () => defaultWorkspaceTabs(),
  merge: normalizePersistedState,
  partialize: state => ({
    openedKeys: state.openedKeys,
    lastUrls: state.lastUrls,
  }),
}));

export function clearWorkspaceTabs() {
  useWorkspaceTabs.getState().resetTabs();
  try {
    useWorkspaceTabs.persist?.clearStorage?.();
  } catch (_error) {
    // sessionStorage can be unavailable in restricted browser contexts.
    // The in-memory reset above must still make logout and startup safe.
  }
}

export default useWorkspaceTabs;
